import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MatchingOperator } from '../../../constants';
import type { AudienceForm } from '../../../types';
import { OperatorType } from '../../DimensionChip/OperatorChip';
import { OperatorDropdownChip } from '../../DimensionChip/OperatorDropdownChip';

export type OperatorPath = Extract<
  FieldPath<AudienceForm>,
  | 'normalized_definition.includes.operator'
  | 'normalized_definition.excludes.operator'
  | `normalized_definition.includes.groups.${number}.operator`
  | `normalized_definition.excludes.groups.${number}.operator`
>;

type OperatorSelectorProps = {
  isSubgroup: boolean;
  path: OperatorPath;
  andOperatorDisabled?: boolean;
} & ({ readOnly: true } | { readOnly?: false });

export const OperatorSelector = ({
  isSubgroup,
  path,
  readOnly = false,
  andOperatorDisabled = false,
}: OperatorSelectorProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<AudienceForm>();
  const observableCurrentOperator = useWatch({ name: path, control });
  const dataTest = isSubgroup ? 'subgroup-operator-dropdown' : 'group-operator-dropdown';
  const operatorType = observableCurrentOperator === MatchingOperator.INTERSECTION ? OperatorType.AND : OperatorType.OR;

  const handleChange = useCallback(
    (newOperator: OperatorType) => {
      const mappedOperator = newOperator === OperatorType.AND ? MatchingOperator.INTERSECTION : MatchingOperator.UNION;

      setValue(path, mappedOperator);
    },
    [setValue, path]
  );

  const labelAndChip = (
    <>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t(isSubgroup ? 'Sub-Groups Operator:' : 'Groups Operator:')}
      </Typography>

      {readOnly ? (
        <OperatorDropdownChip
          dataTest={dataTest}
          operator={operatorType}
          readOnly
          andOperatorDisabled={andOperatorDisabled}
        />
      ) : (
        <OperatorDropdownChip
          dataTest={dataTest}
          operator={operatorType}
          onOperatorChange={handleChange}
          andOperatorDisabled={andOperatorDisabled}
        />
      )}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {labelAndChip}
    </Box>
  );
};
